import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RichTextEditor from "@/components/RichTextEditor";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_CONTENT = "<h2>Hello</h2><p>Write your email...</p><p><a href=\"#\">Call to action</a></p>";

export default function TemplateEditorPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", content: DEFAULT_CONTENT, preview_text: "" });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/templates/${id}`);
        setForm({
          name: data.name || "",
          content: data.content || DEFAULT_CONTENT,
          preview_text: data.preview_text || "",
        });
      } catch {
        toast.error("Template not found");
        navigate("/templates");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/templates/${id}`, form);
        toast.success("Template updated");
      } else {
        const { data } = await api.post("/templates", form);
        toast.success("Template created");
        navigate(`/templates/${data.id}/edit`, { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-10 text-sm text-neutral-500 dark:text-neutral-400">Loading…</div>
    );
  }

  return (
    <div data-testid="template-editor-page">
      <div className="border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/templates" className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="font-display font-bold text-xl tracking-tight">
                {isEdit ? "Edit template" : "New template"}
              </h1>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Design a reusable email layout for campaigns.</p>
            </div>
          </div>
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-sm btn-brand font-semibold"
            data-testid="template-save-submit"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving…" : isEdit ? "Update" : "Create"}
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider">Name</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-2 rounded-sm"
            placeholder="Newsletter layout"
            data-testid="template-name-input"
          />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider">Preview text</Label>
          <Input
            value={form.preview_text}
            onChange={(e) => setForm({ ...form, preview_text: e.target.value })}
            className="mt-2 rounded-sm"
            placeholder="Shown in inbox before opening"
            data-testid="template-preview-input"
          />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider">Content</Label>
          <div className="mt-2" data-testid="template-content-editor">
            <RichTextEditor
              value={form.content}
              onChange={(html) => setForm({ ...form, content: html })}
              placeholder="Write your email template..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
